import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isUUID, parsePedidoCode } from "@/utils/pedidoCode";

/**
 * Identidade canônica do Pedido 360: a venda é o pedido-mãe; os filhos são
 * proprietários operacionais dos itens. Nunca atravessar para outro pedido por
 * numero_pedido ou lead_origem_id: esses atalhos misturavam vendas distintas.
 */
async function resolverPedidoMae(pid: string): Promise<string> {
  try {
    const { data: ped } = await supabase
      .from("pedidos")
      .select("id, pedido_mae_id")
      .eq("id", pid)
      .maybeSingle();
    if (!ped) return pid;
    return ped.pedido_mae_id || ped.id;
  } catch {
    return pid;
  }
}

export function useResolvePedidoId(idOrCode: string | undefined) {
  const [pedidoId, setPedidoId] = useState<string | null>(null);
  const [itemSeq, setItemSeq] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (!idOrCode) throw new Error("Parâmetro ausente.");
        let resolvido: string | null = null;
        if (isUUID(idOrCode)) {
          setItemSeq(null);
          resolvido = idOrCode;
        } else {
          const parsed = parsePedidoCode(idOrCode);
          if (!parsed) throw new Error("Código de pedido inválido.");
          setItemSeq(parsed.itemSeq);
          const { data, error } = await supabase
            .from("pedidos")
            .select("id, pedido_mae_id")
            .eq("numero_pedido", parsed.numero)
            .eq("deletado", false)
            .is("pedido_mae_id", null)
            .order("created_at", { ascending: true })
            .limit(1)
            .maybeSingle();
          if (error) throw error;
          if (!data?.id) throw new Error("Pedido não encontrado.");
          resolvido = data.pedido_mae_id || data.id;
        }
        const pedidoMae = await resolverPedidoMae(resolvido);
        if (alive) setPedidoId(pedidoMae);
      } catch (e: any) {
        if (alive) setError(e.message || "Falha ao resolver pedido.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [idOrCode]);

  return { pedidoId, itemSeq, loading, error };
}
